/**
 * walletDiscovery.js
 *
 * Finding "the" wallet via a single `window.ethereum` breaks the moment
 * a user has more than one extension installed — every EVM wallet fights
 * to inject into that same global, so whichever loads last usually wins
 * and the others become invisible or get silently overridden. That's
 * exactly the failure mode that bit single-wallet dapps for years.
 *
 * The fix is EIP-6963 ("Multi Injected Provider Discovery"): each
 * compliant wallet ANNOUNCES itself over a window event instead of
 * fighting for one global, so a dapp can list every wallet the user has
 * and let them pick. MetaMask, Rabby, OKX, Coinbase Wallet and Phantom
 * all support it. This module:
 *
 *   1. Listens for `eip6963:announceProvider` and asks wallets to
 *      announce via `eip6963:requestProvider`.
 *   2. Falls back to a few known legacy globals (`window.ethereum`'s
 *      `.providers` array some multi-wallet setups populate,
 *      `window.okxwallet`, `window.phantom.ethereum`,
 *      `window.trustwallet`) for anything not yet 6963-compliant,
 *      de-duplicated against anything already announced.
 *
 * Every entry is shaped the same way so the rest of the app never has
 * to care which discovery path found it:
 *   { uid, name, icon, provider, rdns }
 */

const REQUEST_EVENT = 'eip6963:requestProvider';
const ANNOUNCE_EVENT = 'eip6963:announceProvider';

/**
 * Runs EIP-6963 discovery for `waitMs` and resolves with whatever
 * announced in that window, then keeps `onUpdate` firing for any late
 * announcements for the life of the listener (call the returned
 * cleanup function to stop listening).
 */
export function discoverWallets(onUpdate, waitMs = 250) {
  const found = new Map(); // uid -> wallet entry

  const emit = () => onUpdate(Array.from(found.values()));

  const onAnnounce = (event) => {
    const { info, provider } = event.detail || {};
    if (!info || !provider) return;
    found.set(info.uuid, {
      uid: info.uuid,
      name: info.name,
      icon: info.icon, // wallet-supplied data: URI, per the EIP-6963 spec
      rdns: info.rdns,
      provider,
      source: 'eip6963',
    });
    emit();
  };

  window.addEventListener(ANNOUNCE_EVENT, onAnnounce);
  window.dispatchEvent(new Event(REQUEST_EVENT));

  const timer = setTimeout(() => {
    // Add legacy fallbacks for anything that didn't announce itself,
    // de-duped against providers we already found via EIP-6963.
    const already = new Set(Array.from(found.values()).map((w) => w.provider));

    const addLegacy = (uid, name, provider) => {
      if (!provider || already.has(provider)) return;
      already.add(provider);
      found.set(uid, { uid, name, icon: null, rdns: null, provider, source: 'legacy' });
    };

    // window.ethereum.providers — populated by some browsers/extensions
    // when multiple legacy (non-6963) wallets are installed together.
    const injected = typeof window !== 'undefined' ? window.ethereum : undefined;
    if (Array.isArray(injected?.providers)) {
      injected.providers.forEach((p, i) => {
        const label = p.isMetaMask ? 'MetaMask' : p.isRabby ? 'Rabby' : p.isCoinbaseWallet ? 'Coinbase Wallet' : `Injected Wallet ${i + 1}`;
        addLegacy(`legacy-multi-${i}`, label, p);
      });
    } else if (injected) {
      const label = injected.isMetaMask ? 'MetaMask' : injected.isRabby ? 'Rabby' : injected.isCoinbaseWallet ? 'Coinbase Wallet' : injected.isBraveWallet ? 'Brave Wallet' : 'Injected Wallet';
      addLegacy('legacy-ethereum', label, injected);
    }

    addLegacy('legacy-okx', 'OKX Wallet', typeof window !== 'undefined' ? window.okxwallet : undefined);
    addLegacy('legacy-phantom', 'Phantom', typeof window !== 'undefined' ? window.phantom?.ethereum : undefined);
    addLegacy('legacy-trust', 'Trust Wallet', typeof window !== 'undefined' ? window.trustwallet : undefined);

    emit();
  }, waitMs);

  return () => {
    window.removeEventListener(ANNOUNCE_EVENT, onAnnounce);
    clearTimeout(timer);
  };
}
